<script setup lang="ts">
import type { PersonData } from '~/utils/crystallize/pieces'
import { useAuthorQuery } from '~/composables/author/useAuthorQuery'

const props = defineProps<{
  data: string | PersonData | null | undefined
}>()

const { data: ibexaAuthor, isPending: isIbexaAuthorPending } = useAuthorQuery(
  computed(() => ({
    author: typeof props.data === 'string' ? props.data : undefined,
  })),
)

const isPending = computed(() => typeof props.data === 'string' && isIbexaAuthorPending.value)

type Author = PersonData & { slug: string | null | undefined }

const ibexaArticleAuthor = computed((): Author | null => {
  const resolvedAuthorObject = ibexaAuthor.value?.response?.docs?.[0]
  if (!resolvedAuthorObject) {
    return null
  }

  return {
    name: resolvedAuthorObject.name,
    title: resolvedAuthorObject.shortBio,
    slug: resolvedAuthorObject.slug,
    image: {
      firstImage: {
        url: resolvedAuthorObject.image,
      },
      images: [{
        url: resolvedAuthorObject.image,
      }],
    },
  }
})

const author = computed(() => {
  if (typeof props.data === 'string') {
    return ibexaArticleAuthor.value
  }

  return props.data ? { ...props.data, slug: null } satisfies Author : null
})
</script>

<template>
  <component :is="$attrs.is || 'div'" v-if="author || isPending" class="flex items-center gap-4 @container">
    <NettbutikkArticleAuthorImage class="hidden @[19rem]:block" :is-loading="isPending" :author="author" />
    <div v-if="isPending" class="w-full flex flex-col gap-3">
      <div class="sr-only">
        Laster forfatterdata
      </div>
      <Skeleton class="h-6 w-4/5 rounded-lg" />
      <Skeleton class="h-5 w-2/3 rounded-md" />
    </div>
    <address v-else-if="author" class="flex flex-col gap-2xs not-italic max-w-full min-w-0">
      <slot :data="author">
        <p v-if="author.name" class="text-lg font-bold" v-text="author.name" />
        <div class="flex flex-col text-caption">
          <span v-if="author.title" class="font-bold line-clamp-3" v-text="author.title" />
          <span v-if="author.contactInfo.phoneNumber">Tlf: <PhoneNumber class="no-underline text-caption" :value="author.contactInfo.phoneNumber" /></span>
          <span v-if="author.contactInfo.email" class="truncate">
            E-post: <a class="no-underline text-caption" :title="author.contactInfo.email" :href="`mailto:${author.contactInfo.email}`" v-text="author.contactInfo.email" />
          </span>
        </div>
      </slot>
    </address>
  </component>
</template>
